import React, { useState } from "react";
import { toast } from "sonner";
import { Crown, Check, Ticket } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useDeliverable } from "@/hooks/useDeliverable";
import { PageHeader } from "@/components/PageParts";
import { Button, Card, Input, Badge } from "@/components/ui";

export default function Upgrade() {
  const { lang, refreshUser } = useAuth();
  const isAr = lang === "ar";
  const [promo, setPromo] = useState("");
  const [busy, setBusy] = useState(false);

  const { data: pricing } = useDeliverable(() => api.getPricing(), []);
  const { data: sub } = useDeliverable(() => api.getMySubscription(), []);
  const { data: methods } = useDeliverable(() => api.listPaymentMethods(), []);

  const plans = pricing?.plans || pricing?.tiers || [];

  async function redeem() {
    if (!promo) return;
    setBusy(true);
    try {
      await api.redeemPromo(promo.trim());
      await refreshUser();
      toast.success(isAr ? "تم تفعيل الرمز" : "Promo redeemed");
      setPromo("");
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <PageHeader eyebrow={isAr ? "الترقية" : "Upgrade"} title={isAr ? "الاشتراك والخطط" : "Subscription & plans"} right={sub && <Badge tone={sub.is_pro ? "success" : "muted"}>{sub.tier || (sub.is_pro ? "PRO" : "FREE")}</Badge>} />

      <Card className="mb-4 border-2 border-primary">
        <div className="flex items-center gap-2"><Crown className="text-primary" size={20} /><span className="font-black">{isAr ? "الوضع المجاني مُفعّل حالياً" : "Freemium mode is active"}</span></div>
        <p className="mt-2 text-sm text-muted-foreground">{isAr ? "كل الميزات متاحة مجاناً الآن - أنشئ وصدّر بلا حدود." : "All features are free right now - create and export without limits."}</p>
      </Card>

      {plans.length > 0 && (
        <div className="mb-4 grid gap-4 sm:grid-cols-2">
          {plans.map((p, i) => (
            <Card key={i}>
              <div className="text-lg font-black">{isAr ? p.name_ar || p.name : p.name_en || p.name}</div>
              <div className="my-2 text-2xl font-black text-primary">{p.price != null ? `${p.price} ${p.currency || ""}` : "—"}</div>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                {(p.features || p.perks || []).map((f, j) => <li key={j} className="flex items-center gap-2"><Check size={14} className="text-emerald-400" /> {typeof f === "string" ? f : f.label}</li>)}
              </ul>
            </Card>
          ))}
        </div>
      )}

      <Card className="mb-4">
        <p className="mb-3 flex items-center gap-2 text-sm font-black"><Ticket size={16} /> {isAr ? "رمز ترويجي" : "Promo code"}</p>
        <div className="flex gap-2">
          <Input value={promo} onChange={(e) => setPromo(e.target.value)} placeholder={isAr ? "أدخل الرمز" : "Enter code"} data-testid="promo-input" className="flex-1" />
          <Button loading={busy} onClick={redeem} data-testid="redeem-btn">{isAr ? "تفعيل" : "Redeem"}</Button>
        </div>
      </Card>

      {(methods?.items || methods || []).length > 0 && (
        <Card>
          <p className="mb-3 text-sm font-black">{isAr ? "طرق الدفع للتفعيل اليدوي" : "Payment methods for manual activation"}</p>
          <div className="space-y-2">
            {(methods?.items || methods || []).map((m, i) => (
              <div key={i} className="rounded-lg bg-black/20 p-3 text-sm">
                <div className="font-bold">{m.name || m.title}</div>
                <div className="text-xs text-muted-foreground">{m.details || m.instructions || m.account}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
